import type { CaseStatus, QuoteStatus } from '@/types/domain'

/* Which TONE a lifecycle state wears, for every surface that has to colour one.
 *
 * Its own module rather than a pair of constants inside StatusPill, because the
 * pill is no longer the only reader: the quote board tints a lane by the status
 * it holds, and a lane and the pill heading it must mean one thing. Two maps
 * that agree today is how they come to disagree next release. (Mechanically, a
 * component file that also exports constants loses fast refresh — which is what
 * makes this the file the rule wants anyway.)
 *
 * The maps hold the tone ONLY; the label is looked up from the `caseStatus` and
 * `quoteStatus` catalogues at render, keyed by the status itself. They are
 * module-level constants evaluated once at import, so a translated string baked
 * in here would freeze in whichever language was active at startup and would not
 * follow a language switch.
 *
 * Tone is never the whole message. Every surface that reads these pairs the
 * colour with the status in words — roughly one man in twelve has a colour
 * vision deficiency, and this is a product where a misread state is expensive.
 */

export type StatusTone = 'brand' | 'positive' | 'negative' | 'attention' | 'neutral'

export const CASE_TONES: Record<CaseStatus, StatusTone> = {
  draft: 'neutral',
  'in-progress': 'brand',
  stuck: 'attention',
  delivered: 'positive',
  'operationally-closed': 'neutral',
  'financially-closed': 'positive',
  cancelled: 'neutral',
}

export const QUOTE_TONES: Record<QuoteStatus, StatusTone> = {
  draft: 'neutral',
  sent: 'brand',
  rejected: 'negative',
  expired: 'attention',
  // The only positive outcome, and the only one there is: an accepted offer is
  // a case (FR-19). `approved` shared this tone until migration 0050 removed
  // the state.
  converted: 'positive',
}
